import {
  ChevronsUpDown,
  Inbox,
  LogOut,
  Shield,
  SquareUser,
  Sun,
  Moon,
  Monitor,
} from "lucide-react";
import { Link } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import { useIntercom } from "react-use-intercom";

import { useTheme } from "@/components/theme-provider";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

import {
  useIdentity,
  useIdentityContext,
  User,
} from "contexts/identity-context";
import { getFullName, logOut } from "components/navigation/menu-bar";
import { stringAvatar } from "components/shared/shared";
import { TruncatedText } from "components/shared/truncated-text";
import * as Routes from "routes";

export function NavUser() {
  const { isMobile } = useSidebar();
  const [identityState] = useIdentity();
  const { signOut } = useIdentityContext();
  const { setTheme } = useTheme();
  const { shutdown } = useIntercom();
  const posthog = usePostHog();

  const identityName = getFullName(identityState);
  const email = (identityState as User).email;
  const avatarData = stringAvatar(identityName);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarFallback className="rounded-lg text-xs">
                  {avatarData.children}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                <TruncatedText as="span" className="font-medium max-w-full">
                  {identityName}
                </TruncatedText>
                <TruncatedText as="span" className="text-xs max-w-full">
                  {email}
                </TruncatedText>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg text-xs">
                    {avatarData.children}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                  <TruncatedText as="span" className="font-medium max-w-full">
                    {identityName}
                  </TruncatedText>
                  <TruncatedText
                    as="span"
                    className="text-xs text-muted-foreground max-w-full"
                  >
                    {email}
                  </TruncatedText>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to={Routes.userPendingInvitations}>
                  <Inbox />
                  Pending Invitations
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={Routes.userGeneralSettings}>
                  <SquareUser />
                  User Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={Routes.userSecurity}>
                  <Shield />
                  Security Settings
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Sun className="dark:hidden" />
                <Moon className="hidden dark:block" />
                Theme
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => setTheme("light")}>
                  <Sun />
                  Light
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("dark")}>
                  <Moon />
                  Dark
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("system")}>
                  <Monitor />
                  System
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() =>
                logOut(signOut, shutdown, posthog)
              }
            >
              <LogOut />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
